import { useState } from 'react';

const metrics = [
  { label: 'Approach Stability', value: 94, color: '#00FFD1', icon: 'ri-focus-3-line', delta: '+3' },
  { label: 'Airspeed Control', value: 96, color: '#00A3E0', icon: 'ri-speed-line', delta: '+1' },
  { label: 'Radio Communication', value: 89, color: '#B4FF39', icon: 'ri-radio-line', delta: '+5' },
  { label: 'Pattern Geometry', value: 91, color: '#00FFD1', icon: 'ri-map-2-line', delta: '+2' },
];

const flightHistory = [
  { flight: 'F1', score: 72, approach: 68, airspeed: 74, radio: 70, date: 'Mar 3' },
  { flight: 'F2', score: 78, approach: 75, airspeed: 80, radio: 76, date: 'Mar 7' },
  { flight: 'F3', score: 81, approach: 79, airspeed: 83, radio: 80, date: 'Mar 12' },
  { flight: 'F4', score: 85, approach: 84, airspeed: 87, radio: 83, date: 'Mar 18' },
  { flight: 'F5', score: 88, approach: 87, airspeed: 90, radio: 85, date: 'Mar 24' },
  { flight: 'F6', score: 90, approach: 90, airspeed: 92, radio: 87, date: 'Apr 1' },
  { flight: 'F7', score: 92, approach: 92, airspeed: 94, radio: 88, date: 'Apr 6' },
  { flight: 'F8', score: 94, approach: 94, airspeed: 96, radio: 89, date: 'Apr 11' },
];

const chartH = 140;
const chartW = 700;

function getPoints(data: number[], key: keyof typeof flightHistory[0]) {
  return flightHistory
    .map((f, i) => {
      const v = f[key] as number;
      const x = (i / (flightHistory.length - 1)) * (chartW - 20) + 10;
      const y = chartH - ((v / 100) * chartH * 0.88) - 8;
      return `${x},${y}`;
    })
    .join(' ');
}

function AirspeedTape({ speed }: { speed: number }) {
  const ticks = Array.from({ length: 13 }, (_, i) => speed - 30 + i * 5);
  return (
    <div className="relative bg-[#050A14] border border-gray-700 rounded-xl overflow-hidden" style={{ width: 64, height: 180 }}>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-0">
        {ticks.map((v, i) => {
          const isCurrent = v === speed;
          const dist = Math.abs(v - speed);
          return (
            <div key={i} className={`flex items-center w-full px-1 ${isCurrent ? 'bg-[#00A3E0]/20' : ''}`} style={{ height: 13 }}>
              <div className={`h-px flex-1 ${dist === 0 ? 'bg-[#00A3E0]' : dist <= 5 ? 'bg-gray-600' : 'bg-gray-800'}`}></div>
              {v % 10 === 0 && (
                <span className={`text-right font-mono ml-1 ${isCurrent ? 'text-[#00A3E0] font-bold text-xs' : 'text-gray-600 text-xs'}`} style={{ fontSize: 9, minWidth: 24 }}>
                  {v}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 flex items-center pointer-events-none">
        <div className="h-px flex-1 bg-[#00A3E0]"></div>
        <div className="w-2 h-2 bg-[#00A3E0] rotate-45 mr-1"></div>
      </div>
      <div className="absolute bottom-1 left-0 right-0 text-center">
        <span className="text-xs text-gray-500 uppercase tracking-widest" style={{ fontSize: 8 }}>KIAS</span>
      </div>
    </div>
  );
}

function AltitudeTape({ alt }: { alt: number }) {
  const step = 100;
  const ticks = Array.from({ length: 11 }, (_, i) => Math.round((alt - 500 + i * 100) / step) * step);
  return (
    <div className="relative bg-[#050A14] border border-gray-700 rounded-xl overflow-hidden" style={{ width: 64, height: 180 }}>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {ticks.map((v, i) => {
          const isCurrent = v === alt;
          const dist = Math.abs(v - alt);
          return (
            <div key={i} className={`flex items-center w-full px-1 ${isCurrent ? 'bg-[#00FFD1]/20' : ''}`} style={{ height: 16 }}>
              <span className={`font-mono text-right ${isCurrent ? 'text-[#00FFD1] font-bold' : 'text-gray-700'}`} style={{ fontSize: 9, minWidth: 28 }}>
                {v % 200 === 0 ? v : ''}
              </span>
              <div className={`h-px flex-1 ml-1 ${dist === 0 ? 'bg-[#00FFD1]' : dist <= 100 ? 'bg-gray-600' : 'bg-gray-800'}`}></div>
            </div>
          );
        })}
      </div>
      <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 flex items-center pointer-events-none">
        <div className="w-2 h-2 bg-[#00FFD1] rotate-45 ml-1"></div>
        <div className="h-px flex-1 bg-[#00FFD1]"></div>
      </div>
      <div className="absolute bottom-1 left-0 right-0 text-center">
        <span className="text-xs text-gray-500 uppercase tracking-widest" style={{ fontSize: 8 }}>ALT ft</span>
      </div>
    </div>
  );
}

export default function FeatureFlightScoring() {
  const [hoveredFlight, setHoveredFlight] = useState<number | null>(null);
  const [activeMetric, setActiveMetric] = useState<'all' | 'approach' | 'airspeed' | 'radio'>('all');

  const lines: { key: keyof typeof flightHistory[0]; color: string; label: string }[] = [
    { key: 'score', color: '#B4FF39', label: 'Overall' },
    { key: 'approach', color: '#00FFD1', label: 'Approach' },
    { key: 'airspeed', color: '#00A3E0', label: 'Airspeed' },
    { key: 'radio', color: '#F59E0B', label: 'Radio' },
  ];

  return (
    <section id="flight-scoring" className="py-12 sm:py-24 bg-[#08121F]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header - Stack on mobile */}
        <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-12 sm:mb-16">
          <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-2xl flex-shrink-0">
            <i className="ri-bar-chart-box-line text-2xl sm:text-3xl text-[#00FFD1]"></i>
          </div>
          <div>
            <div className="text-xs text-[#00FFD1] uppercase tracking-widest font-semibold mb-2">Capability 01</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">Objective Flight Scoring</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-3xl leading-relaxed">
              Every flight is automatically graded across four core dimensions. No subjectivity — just clean, data-driven scores that tell you exactly where you excelled and where to improve.
            </p>
          </div>
        </div>

        {/* Top Row: HUD Instruments + Gauges */}
        <div className="grid lg:grid-cols-3 gap-6 sm:gap-8 mb-6 sm:mb-8">

          {/* Instrument Panel */}
          <div className="bg-gradient-to-br from-[#0D1B2E] to-[#050A14] rounded-3xl p-4 sm:p-6 border border-[#00FFD1]/20 shadow-2xl">
            <div className="flex items-center justify-between mb-4 sm:mb-5">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Live Instruments</h3>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-[#00FFD1] animate-pulse"></div>
                <span className="text-xs text-[#00FFD1]">Recording</span>
              </div>
            </div>

            {/* Attitude Indicator - Scale down on mobile */}
            <div className="relative mx-auto mb-4 sm:mb-5 rounded-full overflow-hidden border-4 border-gray-700 shadow-inner w-32 h-32 sm:w-40 sm:h-40" style={{ maxWidth: 160, maxHeight: 160 }}>
              <div className="absolute inset-0 bg-gradient-to-b from-[#1A6B9A] to-[#1A6B9A]" style={{ clipPath: 'inset(0 0 50% 0)' }}></div>
              <div className="absolute inset-0 bg-[#8B6914]" style={{ clipPath: 'inset(50% 0 0 0)' }}></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-full h-px bg-[#FFD700] opacity-80"></div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 sm:w-16 h-0.5 bg-[#FFD700]" style={{ marginTop: -1 }}></div>
                <div className="absolute w-3 h-3 rounded-full border-2 border-[#FFD700] bg-transparent"></div>
              </div>
              {/* Tick marks */}
              {[-30, -20, -10, 0, 10, 20, 30].map((deg) => (
                <div
                  key={deg}
                  className="absolute left-1/2 top-1/2 origin-bottom"
                  style={{ transform: `translateX(-50%) translateY(-100%) rotate(${deg}deg)`, height: 70, width: 1 }}
                >
                  <div className={`w-full ${deg === 0 ? 'h-4 bg-white' : 'h-2 bg-gray-400'}`}></div>
                </div>
              ))}
              <div className="absolute bottom-2 sm:bottom-3 left-0 right-0 text-center">
                <span className="text-xs font-bold text-white font-mono">ATT</span>
              </div>
            </div>

            {/* Airspeed + Altitude tapes - Center on mobile */}
            <div className="flex items-center justify-center sm:justify-between gap-2 sm:gap-3">
              <div className="flex flex-col items-center gap-1">
                <AirspeedTape speed={85} />
                <span className="text-xs text-gray-500 uppercase tracking-widest mt-1" style={{ fontSize: 9 }}>Airspeed</span>
              </div>

              {/* VSI */}
              <div className="flex flex-col items-center gap-2">
                <div className="relative bg-[#050A14] border border-gray-700 rounded-full flex items-center justify-center" style={{ width: 72, height: 72 }}>
                  <svg viewBox="0 0 72 72" className="w-full h-full absolute inset-0">
                    <circle cx="36" cy="36" r="32" stroke="#1A2942" strokeWidth="4" fill="none" />
                    {[-500, -250, 0, 250, 500].map((v, i) => {
                      const angle = (v / 1000) * 120 - 90;
                      const rad = (angle * Math.PI) / 180;
                      const x1 = 36 + 26 * Math.cos(rad);
                      const y1 = 36 + 26 * Math.sin(rad);
                      const x2 = 36 + 30 * Math.cos(rad);
                      const y2 = 36 + 30 * Math.sin(rad);
                      return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#4A5568" strokeWidth="1.5" />;
                    })}
                    <line
                      x1="36" y1="36"
                      x2={36 + 22 * Math.cos(((200 / 1000) * 120 - 90) * Math.PI / 180)}
                      y2={36 + 22 * Math.sin(((200 / 1000) * 120 - 90) * Math.PI / 180)}
                      stroke="#00FFD1" strokeWidth="2" strokeLinecap="round"
                    />
                    <circle cx="36" cy="36" r="3" fill="#00FFD1" />
                  </svg>
                  <span className="text-xs font-bold text-[#00FFD1] font-mono z-10" style={{ fontSize: 8 }}>VSI</span>
                </div>
                <div className="bg-[#050A14] border border-gray-700 rounded-lg px-2 py-1 text-center">
                  <p className="text-xs font-bold text-[#B4FF39] font-mono">285°</p>
                  <p className="text-gray-600 uppercase tracking-widest" style={{ fontSize: 8 }}>HDG</p>
                </div>
              </div>

              <div className="flex flex-col items-center gap-1">
                <AltitudeTape alt={1000} />
                <span className="text-xs text-gray-500 uppercase tracking-widest mt-1" style={{ fontSize: 9 }}>Altitude</span>
              </div>
            </div>
          </div>

          {/* Metric Gauges - 2-col on mobile */}
          <div className="lg:col-span-2 bg-gradient-to-br from-[#0D1B2E] to-[#0A1628] rounded-3xl p-5 sm:p-8 border border-[#00FFD1]/20 shadow-2xl">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 sm:mb-8 gap-3">
              <h3 className="text-sm sm:text-lg font-semibold text-white uppercase tracking-wider">Latest Flight Report</h3>
              <span className="text-xs text-gray-500 bg-[#0A1628] border border-gray-700 px-3 py-1 rounded-full whitespace-nowrap">Apr 11, 2025</span>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {metrics.map((m, i) => {
                const circumference = 2 * Math.PI * 46;
                const offset = circumference - (circumference * m.value) / 100;
                return (
                  <div key={i} className="text-center group cursor-default">
                    <div className="relative w-20 h-20 sm:w-28 sm:h-28 mx-auto mb-2 sm:mb-3">
                      {/* Background ring */}
                      <svg className="w-full h-full -rotate-90" viewBox="0 0 112 112">
                        <circle cx="56" cy="56" r="46" stroke="#1A2942" strokeWidth="10" fill="none" />
                        {/* Tick marks */}
                        {Array.from({ length: 20 }).map((_, t) => {
                          const angle = (t / 20) * 360;
                          const rad = (angle * Math.PI) / 180;
                          const x1 = 56 + 50 * Math.cos(rad);
                          const y1 = 56 + 50 * Math.sin(rad);
                          const x2 = 56 + 54 * Math.cos(rad);
                          const y2 = 56 + 54 * Math.sin(rad);
                          return <line key={t} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#1A2942" strokeWidth="1" />;
                        })}
                        <circle
                          cx="56" cy="56" r="46"
                          stroke={m.color}
                          strokeWidth="10"
                          fill="none"
                          strokeDasharray={`${circumference}`}
                          strokeDashoffset={offset}
                          strokeLinecap="round"
                          style={{ filter: `drop-shadow(0 0 6px ${m.color}80)` }}
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-2xl sm:text-3xl font-bold" style={{ color: m.color }}>{m.value}</span>
                        <span className="text-xs font-semibold" style={{ color: m.color, opacity: 0.7 }}>{m.delta}</span>
                      </div>
                    </div>
                    <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center mx-auto mb-1">
                      <i className={`${m.icon} text-sm sm:text-base`} style={{ color: m.color }}></i>
                    </div>
                    <p className="text-xs font-semibold text-white leading-tight px-1">{m.label}</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-gray-800 grid grid-cols-3 gap-3 sm:gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Overall Score</p>
                <p className="text-2xl sm:text-4xl font-bold text-white">92.5</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">vs. Last Flight</p>
                <p className="text-lg sm:text-2xl font-bold text-[#B4FF39]">+2.5 <i className="ri-arrow-up-line text-base sm:text-lg"></i></p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Personal Best</p>
                <p className="text-lg sm:text-2xl font-bold text-[#00FFD1]">94.0 <i className="ri-trophy-line text-base sm:text-lg"></i></p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Row: Multi-line Trend Chart + Feature Cards */}
        <div className="grid lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Multi-line Chart - Horizontal scroll on mobile */}
          <div className="lg:col-span-2 bg-[#0D1B2E] rounded-3xl p-5 sm:p-8 border border-gray-700">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
              <h3 className="text-sm sm:text-base font-semibold text-white uppercase tracking-wider">Score Trend — All Metrics</h3>
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                {lines.map((l) => (
                  <button
                    key={l.key as string}
                    onClick={() => setActiveMetric(activeMetric === l.key as string ? 'all' : l.key as 'approach' | 'airspeed' | 'radio')}
                    className="flex items-center gap-1.5 cursor-pointer"
                  >
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: l.color, opacity: activeMetric === 'all' || activeMetric === l.key ? 1 : 0.3 }}></div>
                    <span className="text-xs text-gray-400">{l.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Chart container with horizontal scroll on mobile */}
            <div className="overflow-x-auto">
              <div className="min-w-[600px]">
                {/* Y-axis labels */}
                <div className="flex gap-3">
                  <div className="flex flex-col justify-between text-right pr-2" style={{ height: chartH }}>
                    {[100, 90, 80, 70].map((v) => (
                      <span key={v} className="text-xs text-gray-700 font-mono">{v}</span>
                    ))}
                  </div>
                  <div className="flex-1 relative">
                    <svg className="w-full" style={{ height: chartH }} viewBox={`0 0 ${chartW} ${chartH}`} preserveAspectRatio="none">
                      <defs>
                        {lines.map((l) => (
                          <linearGradient key={l.key as string} id={`grad-${l.key}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={l.color} stopOpacity="0.15" />
                            <stop offset="100%" stopColor={l.color} stopOpacity="0" />
                          </linearGradient>
                        ))}
                      </defs>

                      {/* Grid lines */}
                      {[0.1, 0.3, 0.6, 0.9].map((t, i) => (
                        <line key={i} x1="0" y1={chartH * t} x2={chartW} y2={chartH * t} stroke="#1A2942" strokeWidth="1" />
                      ))}

                      {lines.map((l) => {
                        const pts = flightHistory.map((f, i) => {
                          const v = f[l.key] as number;
                          const x = (i / (flightHistory.length - 1)) * (chartW - 20) + 10;
                          const y = chartH - ((v / 100) * chartH * 0.88) - 8;
                          return { x, y };
                        });
                        const pointStr = pts.map((p) => `${p.x},${p.y}`).join(' ');
                        const areaStr = [
                          ...pts.map((p) => `${p.x},${p.y}`),
                          `${pts[pts.length - 1].x},${chartH}`,
                          `${pts[0].x},${chartH}`,
                        ].join(' ');
                        const isActive = activeMetric === 'all' || activeMetric === l.key;
                        return (
                          <g key={l.key as string} opacity={isActive ? 1 : 0.15}>
                            <polygon points={areaStr} fill={`url(#grad-${l.key})`} />
                            <polyline points={pointStr} fill="none" stroke={l.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            {pts.map((p, i) => (
                              <circle
                                key={i}
                                cx={p.x} cy={p.y} r={hoveredFlight === i ? 6 : 4}
                                fill={l.color}
                                onMouseEnter={() => setHoveredFlight(i)}
                                onMouseLeave={() => setHoveredFlight(null)}
                                style={{ cursor: 'pointer', transition: 'r 0.15s' }}
                              />
                            ))}
                          </g>
                        );
                      })}

                      {/* Hover tooltip */}
                      {hoveredFlight !== null && (() => {
                        const f = flightHistory[hoveredFlight];
                        const x = (hoveredFlight / (flightHistory.length - 1)) * (chartW - 20) + 10;
                        return (
                          <g>
                            <line x1={x} y1={0} x2={x} y2={chartH} stroke="#ffffff20" strokeWidth="1" strokeDasharray="4 3" />
                            <rect x={x - 40} y={4} width={80} height={22} rx="4" fill="#0D1B2E" stroke="#ffffff20" strokeWidth="1" />
                            <text x={x} y={19} textAnchor="middle" fill="#ffffff" fontSize="11" fontFamily="monospace">{f.date} · {f.score}</text>
                          </g>
                        );
                      })()}
                    </svg>

                    {/* X-axis */}
                    <div className="flex justify-between mt-2 px-1">
                      {flightHistory.map((f, i) => (
                        <div key={i} className="text-center">
                          <p className="text-xs text-gray-600 font-mono">{f.flight}</p>
                          <p className="text-xs text-gray-700" style={{ fontSize: 9 }}>{f.date}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature Cards - Stack to single column on mobile */}
          <div className="grid grid-cols-1 gap-4 content-start">
            {[
              { icon: 'ri-focus-3-line', label: 'Fully Automated', desc: 'No manual input required during flight', color: '#00FFD1' },
              { icon: 'ri-history-line', label: 'Stored Forever', desc: 'Access any past flight score anytime', color: '#00A3E0' },
              { icon: 'ri-share-line', label: 'Share with CFI', desc: 'One-tap report sharing with your instructor', color: '#B4FF39' },
              { icon: 'ri-trophy-line', label: 'Personal Bests', desc: 'Track and celebrate your top performances', color: '#00FFD1' },
            ].map((item, i) => (
              <div key={i} className="bg-[#0F1F35] rounded-xl p-4 sm:p-5 border border-gray-700 hover:border-[#00FFD1]/40 transition-all flex items-start gap-3 sm:gap-4">
                <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: `${item.color}15` }}>
                  <i className={`${item.icon} text-lg sm:text-xl`} style={{ color: item.color }}></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-white mb-0.5">{item.label}</p>
                  <p className="text-xs text-gray-500 leading-snug">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}